/**
 * Shared types for the AI Wedding Planner agentic chat module.
 */

import type {
  AccommodationRoom,
  AccommodationVenue,
  BudgetItem,
  ChatMessage,
  DayPlanEvent,
  Guest,
  GuestGroup,
  RoomElement,
  SeatingPlan,
  SeatingSetupAnswers,
  Table,
  Task,
  Wedding,
} from "@amazing-wedding/shared-types";
import type { PlannerOperations } from "./operations";

/**
 * A file attached to a chat message (image or PDF).
 * Platform UIs may also keep a local preview URI alongside base64 payload.
 */
export interface ChatAttachment {
  id: string;
  mimeType: string;
  base64: string;
  filename?: string;
  previewUrl?: string;
}

/** A web source surfaced by Gemini's Google Search grounding. */
export interface GroundingSource {
  title: string;
  uri: string;
  domain?: string;
  snippet?: string;
}

/** A single tool invocation recorded for display inside an assistant message. */
export interface ToolActivity {
  id: string;
  /** Localised, human-readable summary, e.g. "Dodanie gościa: Anna Nowak". */
  label: string;
  outcome: "done" | "cancelled" | "error";
}

/** A chat message plus the planner-specific render extras. */
export interface PlannerMessage extends ChatMessage {
  /** Tool calls performed while producing this assistant message. */
  activities?: ToolActivity[];
  /** Web sources cited via Google Search grounding. */
  sources?: GroundingSource[];
  /** True when the message reports a failure rather than a normal reply. */
  isError?: boolean;
  /** Files attached to this user message (images / PDFs). */
  attachments?: ChatAttachment[];
}

/**
 * Live read-only accessors handed to every tool executor. Each getter reads
 * the latest Firestore snapshot so the agent always sees current data, even
 * mid-conversation.
 */
export interface ToolContext {
  weddingId: string;
  wedding: () => Wedding;
  guests: () => Guest[];
  tasks: () => Task[];
  budgetItems: () => BudgetItem[];
  tables: () => Table[];
  groups: () => GuestGroup[];
  seatingPlanId: () => string | null;
  seatingPlan: () => SeatingPlan | null;
  venues: () => AccommodationVenue[];
  rooms: () => AccommodationRoom[];
  /** Canvas room elements (dance floor, stage, DJ booth, etc.) — optional; mobile adapters may not supply this yet. */
  roomElements?: () => RoomElement[];
  /** Wedding-day timeline events (Plan dnia) — optional; mobile adapters may not supply this yet. */
  dayPlanEvents?: () => DayPlanEvent[];
  /** Collects web sources from search_web for display in the source badge popover. */
  appendSources?: (sources: GroundingSource[]) => void;
  /** True when the signed-in user is the shared demo account. */
  isDemoAccount: () => boolean;
  /** Platform Firestore + domain writes (web / iOS / Android adapters). */
  operations: PlannerOperations;
  /** Localised strings for tool labels and errors (i18next on web, RN i18n on mobile). */
  translate: (key: string, params?: Record<string, unknown>) => string;
}

/** Headline numbers injected into the system prompt at session start. */
export interface WeddingContextSummary {
  coupleName1: string;
  coupleName2: string;
  weddingDate: string;
  daysUntil: number | null;
  venue: string | null;
  currency: string;
  language: "pl" | "en" | "de";
  guestTotal: number;
  guestAccepted: number;
  guestDeclined: number;
  guestPending: number;
  taskTotal: number;
  taskDone: number;
  budgetTotal: number;
  budgetSpent: number;
  activeSeatingPlanName: string | null;
  tableCount: number;
}

/** A destructive action awaiting the user's approval in the UI. */
export interface PendingConfirmation {
  toolName: string;
  /** Localised description of what will happen if approved. */
  description: string;
}

/** Seating setup questionnaire shown before setup_seating_plan. */
export interface PendingSeatingSetupQuestionnaire {
  toolName: string;
  /** Fresh per request so the dialog only resets when the agent reissues. */
  requestId: string;
  intro?: string;
  totalHeads: number;
  defaults: SeatingSetupAnswers;
}

/** Stable id for the initial greeting bubble — skip enter animation on mount. */
export const PLANNER_GREETING_ID = "greeting";

export type PlannerStatus =
  | "idle"
  | "thinking"
  | "awaiting-confirmation"
  | "awaiting-questionnaire";

/** Result of one full agent turn (user message → final assistant reply). */
export interface AgentTurnResult {
  text: string;
  activities: ToolActivity[];
  sources: GroundingSource[];
}
